import { useState, useEffect } from "react";
import { Check, Crown, Coins, Loader2, Settings2, X, CalendarClock, Gift, Zap, Sparkles, Building2 } from "lucide-react";
import { Trans, useTranslation } from "react-i18next";
import { toast } from "sonner";
import api, { fmtCredits } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import SeatBadge, { fmtWeekEnd } from "./SeatBadge";

// "gratis" fuera (ago-2026): dejó de ser un plan elegible y pasó a ser el estado de una cuenta
// sin acceso. Ofrecer aquí "bajar a Gratis" equivalía a ofrecer perder el producto.
const PLAN_ORDER = ["starter", "creator", "pro", "studio"];
const PLAN_RANK = { gratis: 0, starter: 1, creator: 2, pro: 3, studio: 4 };
const PLAN_ICON = { gratis: Gift, starter: Zap, creator: Sparkles, pro: Crown, studio: Building2 };

export default function UpgradeModal({ open, detail, onClose, quota, checkout, openPortal, changePlan, cancelPendingChange }) {
  const { t, i18n } = useTranslation();
  const { user } = useAuth();
  const [busy, setBusy] = useState(null);
  const [seatsFullPlan, setSeatsFullPlan] = useState(null);
  const [waitlisted, setWaitlisted] = useState(null);
  const [seats, setSeats] = useState(null); // plazas restantes por plan — aviso ANTES de intentar pagar
  const [billingPeriod, setBillingPeriod] = useState("monthly"); // "monthly" | "annual" -- solo aplica a altas nuevas (checkout)
  const plans = quota?.plans || {};
  const current = quota?.plan;
  const hasSub = quota?.has_subscription;
  const pending = quota?.pending_plan_change;
  const fmtDate = (iso) => {
    if (!iso) return "";
    try { return new Date(iso).toLocaleDateString(i18n.language === "en" ? "en-US" : "es-ES", { day: "numeric", month: "long", year: "numeric" }); }
    catch { return ""; }
  };

  useEffect(() => {
    if (!open) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const onKey = (e) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", onKey);
    return () => { document.body.style.overflow = prev; window.removeEventListener("keydown", onKey); };
  }, [open, onClose]);

  // Aviso de escasez ANTES de que el usuario intente pagar (no solo tras un fallo de checkout,
  // que es lo único que había antes — ver seatsFullPlan más abajo, que sigue como red de
  // seguridad si las plazas se agotan justo entre que se abre el modal y se pulsa "elegir").
  useEffect(() => {
    if (!open) return;
    api.get("/plan-seats").then((r) => setSeats(r.data?.plans || {})).catch(() => setSeats({}));
  }, [open]);

  const pick = async (key) => {
    if (key === current || (pending && pending.plan === key)) return;
    setBusy(key);
    setSeatsFullPlan(null);
    try {
      if (hasSub) {
        const data = await changePlan(key);
        if (data?.applied === "now") toast.success(t("upgrade.updatedNow"));
        else toast.success(t("upgrade.willChange", { plan: plans[key]?.label || key, date: fmtDate(data?.effective_at) }));
        onClose();
      } else {
        await checkout(key, billingPeriod); // alta nueva: redirige a Stripe
      }
    } catch (e) {
      const d = e?.response?.data?.detail;
      if (d?.error === "seats_full") { setSeatsFullPlan(key); setWaitlisted(null); }
      const msg = d?.msg || d;
      toast.error(typeof msg === "string" ? msg : t("upgrade.genericError"));
      setBusy(null);
    }
  };

  const joinWaitlist = async (key) => {
    if (!user?.email) return;
    setBusy(key);
    try {
      await api.post("/plan-seats/waitlist", { email: user.email, plan: key });
      setWaitlisted(key);
    } catch (e) { toast.error(t("upgrade.genericError")); }
    setBusy(null);
  };

  const cancelPending = async () => {
    setBusy("cancel-pending");
    try {
      await cancelPendingChange();
      toast.success(t("upgrade.cancelledKeep"));
    } catch (e) { toast.error(t("upgrade.cancelError")); }
    setBusy(null);
  };

  const manage = async () => {
    setBusy("portal");
    try { await openPortal(); } catch (e) { toast.error(t("upgrade.portalError")); setBusy(null); }
  };

  if (!open) return null;

  const labelFor = (key) => {
    if (key === current) return t("upgrade.labelCurrent");
    if (pending && pending.plan === key) return t("upgrade.labelScheduled");
    if (PLAN_RANK[key] > PLAN_RANK[current]) return hasSub ? t("upgrade.labelUpgrade") : t("upgrade.labelSubscribe");
    return t("upgrade.labelChange");
  };

  return (
    <div className="fixed inset-0 z-50 bg-slate-900/70 backdrop-blur-sm overflow-y-auto vb-scroll" onClick={onClose} data-testid="upgrade-overlay">
      <div className="min-h-full flex items-start sm:items-center justify-center sm:p-6 lg:p-10">
        <div data-testid="upgrade-modal" onClick={(e) => e.stopPropagation()}
          className="relative bg-white w-full sm:max-w-2xl md:max-w-4xl lg:max-w-6xl xl:max-w-7xl min-h-screen sm:min-h-0 sm:rounded-3xl shadow-2xl p-5 sm:p-8 lg:p-10">
          <button data-testid="upgrade-close" onClick={onClose} className="absolute right-4 top-4 z-10 text-slate-400 hover:text-slate-700 hover:bg-slate-100 rounded-full p-2 transition-colors"><X size={24} /></button>

          <div className="text-center sm:text-left mb-5">
            <h2 className="text-2xl sm:text-3xl font-bold text-slate-900 flex items-center justify-center sm:justify-start gap-2 pr-10">
              <Crown className="text-amber-500 shrink-0" size={28} /> {t("upgrade.title")}
            </h2>
          </div>

          {detail?.msg && (
            <div data-testid="upgrade-reason" className="bg-amber-50 border-2 border-amber-200 text-amber-800 rounded-xl px-4 py-3 text-base font-medium mb-4">
              {detail.msg}
            </div>
          )}

          {pending && (
            <div data-testid="pending-change-banner" className="bg-blue-50 border-2 border-blue-200 text-blue-800 rounded-xl px-4 py-3 mb-4 flex flex-col sm:flex-row sm:items-center gap-2">
              <CalendarClock size={20} className="shrink-0" />
              <span className="flex-1 text-base font-medium"><Trans i18nKey="upgrade.scheduledChangeBanner" values={{ plan: pending.plan_label, date: fmtDate(pending.effective_at) }} components={{ 0: <b />, 1: <b /> }} /></span>
              <button data-testid="cancel-pending-change" onClick={cancelPending} disabled={busy === "cancel-pending"}
                className="inline-flex items-center justify-center gap-1.5 text-sm font-bold text-blue-700 bg-white border-2 border-blue-200 rounded-lg px-3 py-1.5 hover:bg-blue-100">
                {busy === "cancel-pending" && <Loader2 size={14} className="animate-spin" />} {t("upgrade.cancelPending")}
              </button>
            </div>
          )}

          {/* Se oculta cuando no hay nada que decir: sin suscripción activa NI saldo sobrante,
              esto siempre lee "Gratis · 0 créditos" — no aporta nada sobre el aviso amarillo de
              arriba, y encima el botón "Gestionar suscripción" tampoco se pinta sin hasSub, así
              que el recuadro se quedaría con una sola frase que no dice nada nuevo. Si queda
              saldo de recarga aunque no haya suscripción, sí se muestra: ese dato sí importa. */}
          {quota && (hasSub || (quota.seconds_remaining || 0) > 0) && (
            <div className="flex flex-wrap items-center gap-2 text-sm sm:text-base text-slate-600 bg-slate-50 border border-slate-200 rounded-xl px-4 py-3 mb-6">
              <Coins size={18} className="text-blue-600 shrink-0" />
              <span>
                {t("upgrade.currentPlan")} <b className="text-slate-900">{quota.plan_label}</b> ·
                {" "}{t("upgrade.youHave")} <b className="text-slate-900">{fmtCredits(quota.seconds_remaining || 0)} {t("common.credits", { count: Math.round(quota.seconds_remaining || 0) })}</b> {t("upgrade.planAndTopup", { plan: fmtCredits(quota.plan_seconds || 0), topup: fmtCredits(quota.topup_seconds || 0) })}
              </span>
              {hasSub && (
                <button data-testid="manage-subscription" onClick={manage} disabled={busy === "portal"}
                  className="sm:ml-auto inline-flex items-center gap-1.5 text-sm font-semibold text-blue-700 hover:text-blue-900 underline whitespace-nowrap">
                  {busy === "portal" ? <Loader2 size={14} className="animate-spin" /> : <Settings2 size={14} />}
                  {t("upgrade.manageSub")}
                </button>
              )}
            </div>
          )}

          {!hasSub && (
            <div className="flex justify-center mb-6">
              <div className="inline-flex items-center bg-slate-100 rounded-full p-1.5 gap-1" role="group" aria-label={t("upgrade.title")}>
                <button type="button" onClick={() => setBillingPeriod("monthly")}
                  className={`px-4 py-2 rounded-full text-sm font-bold transition-all ${billingPeriod === "monthly" ? "bg-white text-slate-900 shadow" : "text-slate-500 hover:text-slate-700"}`}>
                  {t("upgrade.billingMonthly")}
                </button>
                <button type="button" onClick={() => setBillingPeriod("annual")}
                  className={`px-4 py-2 rounded-full text-sm font-bold transition-all ${billingPeriod === "annual" ? "bg-white text-slate-900 shadow" : "text-slate-500 hover:text-slate-700"}`}>
                  {t("upgrade.billingAnnual")}
                </button>
              </div>
            </div>
          )}

          <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-5 xl:gap-6 xl:items-stretch">
            {PLAN_ORDER.filter((k) => plans[k]).map((key) => {
              const p = plans[key];
              const isCurrent = key === current;
              const isPending = pending && pending.plan === key;
              const featured = key === "creator";
              const Icon = PLAN_ICON[key] || Sparkles;
              return (
                <div key={key} data-testid={`plan-card-${key}`}
                  className={`relative rounded-2xl border-2 p-5 lg:p-6 flex flex-col transition-shadow
                    ${isPending ? "border-blue-400 ring-2 ring-blue-100 bg-blue-50/40"
                      : featured ? "border-blue-600 shadow-xl xl:-translate-y-2 bg-white z-10"
                      : "border-slate-200 hover:border-slate-300"}`}>
                  {featured && !isPending && (
                    <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-blue-600 text-white text-xs font-bold uppercase tracking-wide px-3 py-1 rounded-full shadow-md whitespace-nowrap">
                      {t("upgrade.mostPopular")}
                    </div>
                  )}
                  {isPending && <div className="text-xs font-bold text-blue-700 uppercase tracking-wide mb-1">{t("upgrade.scheduled")}</div>}
                  <div className={`w-11 h-11 rounded-xl flex items-center justify-center mb-3 ${featured ? "bg-blue-600 text-white" : "bg-slate-100 text-slate-500"}`}>
                    <Icon size={22} />
                  </div>
                  <div className="text-xl font-bold text-slate-900">{p.label}</div>
                  <div className="mt-1 mb-1">
                    {(() => {
                      const showAnnual = !hasSub && billingPeriod === "annual" && p.price > 0;
                      const displayPrice = showAnnual ? p.price * 10 : p.price;
                      return (
                        <>
                          <span className="text-3xl lg:text-4xl font-extrabold text-slate-900">{p.price === 0 ? t("upgrade.free") : `${displayPrice}€`}</span>
                          {p.price > 0 && <span className="text-slate-500 text-sm">{showAnnual ? t("upgrade.perYear") : t("upgrade.perMonth")}</span>}
                        </>
                      );
                    })()}
                  </div>
                  {!hasSub && billingPeriod === "annual" && p.price > 0 && (
                    <p className="text-xs text-emerald-600 font-semibold mb-2">{t("upgrade.annualNote")}</p>
                  )}
                  {(hasSub || billingPeriod !== "annual" || p.price === 0) && <div className="mb-2" />}
                  <ul className="space-y-2 text-base text-slate-700 flex-1">
                    <li className="flex items-center gap-2"><Check size={18} className="text-green-600 shrink-0" /> {t("upgrade.minPerMonth", { credits: fmtCredits(p.seconds || 0) })}</li>
                    {/* Los créditos por sí solos no dicen nada: "4.200" solo significa algo si
                        sabes lo que consume un vídeo. El número de vídeos lo calcula el backend
                        con el consumo real medido, no una regla copiada aquí. */}
                    {p.videos > 0 && (
                      <li className="flex items-center gap-2 font-semibold text-slate-900">
                        <Check size={18} className="text-green-600 shrink-0" />
                        {t("upgrade.videosPerMonth", {
                          count: Math.round(p.videos),
                          min: Math.min(quota?.duracion_referencia_min || 15, p.max_minutes || 15),
                        })}
                      </li>
                    )}
                    <li className="flex items-center gap-2"><Check size={18} className="text-green-600 shrink-0" /> {t("upgrade.channelsCount", { count: p.channels })}</li>
                    {p.max_minutes != null && (
                      <li className="flex items-center gap-2"><Check size={18} className="text-green-600 shrink-0" /> {t("upgrade.maxPerVideo", { min: p.max_minutes })}</li>
                    )}
                    {p.max_storage_gb != null && (
                      <li className="flex items-center gap-2"><Check size={18} className="text-green-600 shrink-0" /> {t("upgrade.storageGb", { gb: p.max_storage_gb })}</li>
                    )}
                    {key === "gratis" && (t("upgrade.feat.gratis", { returnObjects: true }) || []).map((f) => (
                      <li key={f} className="flex items-start gap-2"><Check size={18} className="text-green-600 mt-0.5 shrink-0" /> {f}</li>
                    ))}
                  </ul>
                  {/* Aviso previo de plazas: se oculta si el fallback reactivo de abajo ya está
                      mostrando el mismo aviso para este plan (se agotaron justo al pulsar
                      "elegir") — mostrar los dos a la vez duplicaría el mensaje. */}
                  {seatsFullPlan !== key && (
                    <SeatBadge plan={key} info={seats?.[key]}
                      weekEndLabel={fmtWeekEnd(seats?.[key]?.week_ends_at, i18n.language)}
                      email={user?.email} />
                  )}
                  {seatsFullPlan === key ? (
                    waitlisted === key ? (
                      <div data-testid={`waitlist-success-${key}`} className="mt-5 text-sm font-semibold text-emerald-700 bg-emerald-50 border-2 border-emerald-200 rounded-xl px-3 py-3 text-center">
                        ✅ {t("auth.waitlistJoined")}
                      </div>
                    ) : (
                      <button
                        data-testid={`join-waitlist-plan-${key}`}
                        disabled={!!busy}
                        onClick={() => joinWaitlist(key)}
                        className="mt-5 min-h-[48px] rounded-xl font-bold text-base flex items-center justify-center gap-2 transition-colors bg-amber-500 text-white hover:bg-amber-600">
                        {busy === key && <Loader2 size={18} className="animate-spin" />}
                        {t("auth.joinWaitlist")}
                      </button>
                    )
                  ) : (
                    <button
                      data-testid={`select-plan-${key}`}
                      disabled={isCurrent || isPending || !!busy}
                      onClick={() => pick(key)}
                      className={`mt-5 min-h-[48px] rounded-xl font-bold text-base flex items-center justify-center gap-2 transition-colors
                        ${isCurrent || isPending ? "bg-slate-100 text-slate-400 cursor-default"
                          : featured ? "bg-blue-600 text-white hover:bg-blue-700"
                          : key === "gratis" ? "bg-slate-200 text-slate-700 hover:bg-slate-300"
                          : "bg-slate-900 text-white hover:bg-slate-800"}`}>
                      {busy === key && <Loader2 size={18} className="animate-spin" />}
                      {labelFor(key)}
                    </button>
                  )}
                </div>
              );
            })}
          </div>
          <p className="text-base text-slate-600 font-medium text-center mt-6">{t("upgrade.featShared")}</p>
          <p className="text-sm text-slate-400 text-center mt-2">{t("upgrade.footer")}</p>
        </div>
      </div>
    </div>
  );
}
